import { HelpCircle } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { useUpdateWarmupSpeed, WarmupSpeed } from "@/hooks/use-mailbox";
import { MailboxType } from "@/types/mailbox.types";
import { useEffect } from "react";

interface SpeedOption {
  id: number;
  name: string;
  animal: string;
  description: string;
  days: string;
  emoji: string;
}

const speedOptions: SpeedOption[] = [
  {
    id: 1,
    name: WarmupSpeed.SLOW,
    animal: "Tortoise",
    description: "Gradually increase email sending with maximum safety",
    days: "~45 days",
    emoji: "🐢",
  },
  {
    id: 2,
    name: WarmupSpeed.MEDIUM,
    animal: "Rabbit",
    description: "Balanced approach to warm up your mailbox",
    days: "~30 days",
    emoji: "🐰",
  },
  {
    id: 3,
    name: WarmupSpeed.FAST,
    animal: "Leopard",
    description: "Quickly reach your target sending volume",
    days: "~15 days",
    emoji: "🐆",
  },
];

const DEFAULT_SPEED_INDEX = 1;

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mailbox: MailboxType | undefined;
}

const getTheSpeedIndex = (mailbox: MailboxType | undefined) => {
  if (!mailbox) return DEFAULT_SPEED_INDEX;

  const speedIndex = speedOptions.findIndex(
    (option) => option.name.toLowerCase() === mailbox.warmupSpeed?.toLowerCase()
  );
  return speedIndex !== -1 ? speedIndex : DEFAULT_SPEED_INDEX;
};

export function SettingsDialog({
  open,
  onOpenChange,
  mailbox,
}: SettingsDialogProps) {
  const [selectedSpeedIndex, setSelectedSpeedIndex] =
    React.useState<number>(DEFAULT_SPEED_INDEX);

  const { mutateAsync: updateWarmupSpeed, isPending: isUpdatingWarmupSpeed } =
    useUpdateWarmupSpeed();

  const selectedSpeedOption =
    speedOptions[selectedSpeedIndex] || speedOptions[DEFAULT_SPEED_INDEX];

  const handleSave = async () => {
    try {
      if (mailbox?._id) {
        await updateWarmupSpeed({
          id: mailbox._id,
          updates: {
            warmupSpeed: selectedSpeedOption.name as WarmupSpeed,
          },
        });

        window.cioanalytics?.track("mailbox_temperature_changed", {
          email: mailbox?.email,
          speed: selectedSpeedOption.name,
        });
      }
      onOpenChange(false);
    } catch (error) {
      console.error(error);
    }
  };

  // const handleSelectSpeed = async (speed: SpeedOption) => {
  //   try {
  //     if (mailbox?._id) {
  //       await updateWarmupSpeed({
  //         id: mailbox._id,
  //         updates: {
  //           warmupSpeed: speed.name as WarmupSpeed,
  //         },
  //       });
  //     }
  //     onOpenChange(false);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  useEffect(() => {
    const speedIndex = getTheSpeedIndex(mailbox);
    setSelectedSpeedIndex(speedIndex);
  }, [mailbox]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        setSelectedSpeedIndex(getTheSpeedIndex(mailbox));
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4 overflow-y-auto scrollbar-hide">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-600">
                Speed of increasing temperature
              </span>

              <Tooltip>
                <TooltipTrigger tabIndex={-1}>
                  <HelpCircle className="h-4 w-4 text-blue-600" />
                </TooltipTrigger>
                <TooltipContent className="text-white bg-black max-w-xs">
                  <TooltipArrow />
                  The speed of increasing temperature determines how fast you
                  can reach your target sending volume.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="grid gap-3">
              {speedOptions.map((option, index) => (
                <button
                  key={option.id}
                  onClick={() => {
                    // handleSelectSpeed(option);
                    setSelectedSpeedIndex(index);
                  }}
                  className={cn(
                    "flex items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-accent",
                    speedOptions[selectedSpeedIndex].id === option.id &&
                      "border-primary bg-primary/10 hover:bg-primary/20"
                  )}
                >
                  <div className="text-2xl">{option.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{option.animal}</div>
                        <div className="text-sm text-muted-foreground">
                          {option.name}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {option.days}
                      </div>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {option.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="space-y-2 rounded-lg bg-secondary p-4 text-center">
              <div className="font-medium">
                {selectedSpeedOption.name} mode:
              </div>
              <p className="text-sm text-muted-foreground">
                {selectedSpeedIndex === 0 && (
                  <>
                    The safest approach to warm up your mailbox.
                    <br />
                    Recommended for new domains and strict email providers.
                  </>
                )}
                {selectedSpeedIndex === 1 && (
                  <>
                    Balanced approach for most email campaigns.
                    <br />
                    Suitable for established domains with good reputation.
                  </>
                )}
                {selectedSpeedIndex === 2 && (
                  <>
                    Start your email campaign as soon as possible.
                    <br />
                    Use with caution! In some cases, it can negatively affect
                    your reputation.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setSelectedSpeedIndex(getTheSpeedIndex(mailbox));
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isUpdatingWarmupSpeed}
            isLoading={isUpdatingWarmupSpeed}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
